/*
 * File: useSalesOrderService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 15 Nov 2021 2:42:53 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { createApi, fetchBaseQuery, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import { SalesOrderLineItemModel } from 'models/SalesOrderLineItemModel';
import { SalesOrderModel } from 'models/SalesOrderModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { setLineItemSubmitErrors } from 'redux/productsInfo/productsInfoSlice';
import { ReduxState } from 'redux/rootReducer';
import { resetSubmittedCount, setSubmitLoadingMessage } from 'redux/salesOrder/ordersSlice';

/* ****************** Interfaces ****************** */
interface Attachment {
  name: string;
  url: string;
  size: number;
  extension: string;
  createdByUserId: string;
  createdByUserFullName: string;
  createdDateTime: string;
}
interface CreateAttachmentPayload {
  file: FormData;
  salesOrderId: string;
}

interface GetSalesOrdersParams {
  dataAreaId: string | null;
  createdByUserNameContains?: string;
  createdDateTime?: string;
  customerIdContains?: string | null;
  customerNameContains?: string;
  customerPurchaseOrderNumberContains?: string;
  draftIdContains?: string;
  endDate?: string | null;
  projectId?: string;
  quoteIdContains?: string;
  requestedShipDate?: string;
  salesOrderNumberContains?: string;
  salesOrderStatuses?: string;
  salesOrderTypeContains?: string;
  salesPersonNameContains?: string;
  stagingId?: string;
  startDate?: string | null;
  status?: string;
  orderByField?: string;
  orderByDirection?: string;
  offset?: number;
}
interface GetSalesOrderResponseModel {
  data: SalesOrderModel[];
  totalCount: number;
}
interface GetSalesOrderParams {
  id: string;
}
interface CreateSalesOrderParams {
  payload: SalesOrderPayloadModel;
}
interface SubmitSalesOrderParams {
  payload?: SalesOrderPayloadModel;
}
interface SubmitHeaderSalesOrderParams {
  salesOrderId: string;
}
interface SubmitLineItemSalesOrderParams {
  salesOrderId: string;
  lineItemId: string;
}
interface ValidateSalesOrderLineItemParams {
  salesOrderId: string;
  lineItemId: string;
}
interface UpdateSalesOrderParams {
  payload: SalesOrderPayloadModel;
  salesOrderId: string;
}

/* ****************** Variables ****************** */
const mamaBearBaseUrl = process.env.REACT_APP_MAMA_BEAR_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_MAMA_BEAR;

const baseQuery = fetchBaseQuery({
  baseUrl: mamaBearBaseUrl,
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
    }
    if (endpoint !== 'createAttachment' && endpoint !== 'postAttachment') {
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const salesOrderApi = createApi({
  reducerPath: 'salesOrderApi',
  baseQuery,
  tagTypes: ['SalesOrder', 'SalesOrders'],
  endpoints: (builder) => ({
    getSalesOrders: builder.query<GetSalesOrderResponseModel, GetSalesOrdersParams>({
      query: ({
        dataAreaId,
        createdByUserNameContains,
        createdDateTime,
        customerIdContains,
        customerNameContains,
        customerPurchaseOrderNumberContains,
        draftIdContains,
        endDate,
        quoteIdContains,
        requestedShipDate,
        salesOrderNumberContains,
        salesOrderTypeContains,
        salesPersonNameContains,
        stagingId,
        startDate,
        status,
        orderByField,
        orderByDirection,
        offset
      }) => ({
        url: `productSalesOrders`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          createdByUserNameContains: createdByUserNameContains ? encodeValue({ isUrl: false, value: createdByUserNameContains }) : undefined,
          createdDateTime: createdDateTime ? encodeValue({ isUrl: false, value: createdDateTime }) : undefined,
          customerIdContains: customerIdContains ? encodeValue({ isUrl: false, value: customerIdContains }) : undefined,
          customerNameContains: customerNameContains ? encodeValue({ isUrl: false, value: customerNameContains }) : undefined,
          customerPurchaseOrderNumberContains: customerPurchaseOrderNumberContains ? encodeValue({ isUrl: false, value: customerPurchaseOrderNumberContains }) : undefined,
          draftIdContains: draftIdContains ? encodeValue({ isUrl: false, value: draftIdContains }) : undefined,
          endDate: endDate ? encodeValue({ isUrl: false, value: endDate }) : undefined,
          quoteIdContains: quoteIdContains ? encodeValue({ isUrl: false, value: quoteIdContains }) : undefined,
          requestedShipDate: requestedShipDate ? encodeValue({ isUrl: false, value: requestedShipDate }) : undefined,
          salesOrderNumberContains: salesOrderNumberContains ? encodeValue({ isUrl: false, value: salesOrderNumberContains }) : undefined,
          salesOrderTypeContains: salesOrderTypeContains ? encodeValue({ isUrl: false, value: salesOrderTypeContains }) : undefined,
          salesPersonNameContains: salesPersonNameContains ? encodeValue({ isUrl: false, value: salesPersonNameContains }) : undefined,
          stagingId: stagingId ? encodeValue({ isUrl: false, value: stagingId }) : undefined,
          startDate: startDate ? encodeValue({ isUrl: false, value: startDate }) : undefined,
          status: status ? encodeValue({ isUrl: false, value: status }) : undefined,
          orderByField,
          orderByDirection,
          offset
        }
      }),
      providesTags: ['SalesOrders']
    }),
    getDeletedSalesOrders: builder.query<GetSalesOrderResponseModel, GetSalesOrdersParams>({
      query: ({
        dataAreaId,
        createdByUserNameContains,
        createdDateTime,
        customerIdContains,
        customerNameContains,
        customerPurchaseOrderNumberContains,
        draftIdContains,
        endDate,
        quoteIdContains,
        requestedShipDate,
        salesOrderNumberContains,
        salesOrderTypeContains,
        salesPersonNameContains,
        stagingId,
        startDate,
        status,
        orderByField,
        orderByDirection,
        offset
      }) => ({
        url: `productSalesOrders`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          createdByUserNameContains: createdByUserNameContains ? encodeValue({ isUrl: false, value: createdByUserNameContains }) : undefined,
          createdDateTime: createdDateTime ? encodeValue({ isUrl: false, value: createdDateTime }) : undefined,
          customerIdContains: customerIdContains ? encodeValue({ isUrl: false, value: customerIdContains }) : undefined,
          customerNameContains: customerNameContains ? encodeValue({ isUrl: false, value: customerNameContains }) : undefined,
          customerPurchaseOrderNumberContains: customerPurchaseOrderNumberContains ? encodeValue({ isUrl: false, value: customerPurchaseOrderNumberContains }) : undefined,
          draftIdContains: draftIdContains ? encodeValue({ isUrl: false, value: draftIdContains }) : undefined,
          endDate: endDate ? encodeValue({ isUrl: false, value: endDate }) : undefined,
          quoteIdContains: quoteIdContains ? encodeValue({ isUrl: false, value: quoteIdContains }) : undefined,
          requestedShipDate: requestedShipDate ? encodeValue({ isUrl: false, value: requestedShipDate }) : undefined,
          salesOrderNumberContains: salesOrderNumberContains ? encodeValue({ isUrl: false, value: salesOrderNumberContains }) : undefined,
          salesOrderTypeContains: salesOrderTypeContains ? encodeValue({ isUrl: false, value: salesOrderTypeContains }) : undefined,
          salesPersonNameContains: salesPersonNameContains ? encodeValue({ isUrl: false, value: salesPersonNameContains }) : undefined,
          stagingId: stagingId ? encodeValue({ isUrl: false, value: stagingId }) : undefined,
          startDate: startDate ? encodeValue({ isUrl: false, value: startDate }) : undefined,
          status: status ? encodeValue({ isUrl: false, value: status }) : undefined,
          returnDeletedDataOnly: true,
          orderByField,
          orderByDirection,
          offset
        }
      }),
      providesTags: ['SalesOrders']
    }),
    getSalesOrder: builder.query<SalesOrderModel, GetSalesOrderParams>({
      query: ({ id }) => ({ url: `productSalesOrders/${id}` }),
      providesTags: (_result, _error, arg) => [{ type: 'SalesOrder', id: arg.id }]
    }),
    deleteSalesOrder: builder.mutation<void, { productSalesOrderId: string }>({
      query: ({ productSalesOrderId }) => ({
        url: `productSalesOrders/${productSalesOrderId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SalesOrders']
    }),
    purgeSalesOrder: builder.mutation<void, { productSalesOrderId: string }>({
      query: ({ productSalesOrderId }) => ({
        url: `productSalesOrders/${productSalesOrderId}?wouldYouLikeToPlayAGame=true`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SalesOrders']
    }),
    recoverSalesOrder: builder.mutation<SalesOrderModel, { productSalesOrderId: string }>({
      query: ({ productSalesOrderId }) => ({
        url: `productSalesOrders/${productSalesOrderId}/recover`
      }),
      invalidatesTags: ['SalesOrders']
    }),
    createSalesOrder: builder.mutation<SalesOrderModel, CreateSalesOrderParams>({
      query: ({ payload }) => ({ url: `productSalesOrders`, method: 'POST', body: payload })
    }),
    createAttachment: builder.mutation<Attachment, CreateAttachmentPayload>({
      query: ({ file, salesOrderId }) => ({ url: `productSalesOrders/${salesOrderId}/attachments`, method: 'POST', body: file })
    }),
    postAttachment: builder.mutation<Attachment, { salesOrderId: string; fileName: string }>({
      query: ({ fileName, salesOrderId }) => ({ url: `productSalesOrders/${salesOrderId}/attachments/${fileName}`, method: 'POST' }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'SalesOrder', id: arg.salesOrderId }]
    }),
    deleteAttachment: builder.mutation<void, { salesOrderId: string; fileName: string }>({
      query: ({ salesOrderId, fileName }) => ({
        url: `productSalesOrders/${salesOrderId}/attachments/${fileName}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['SalesOrder', 'SalesOrders']
    }),
    updateSalesOrder: builder.mutation<SalesOrderModel, UpdateSalesOrderParams>({
      query: ({ payload, salesOrderId }) => ({ url: `productSalesOrders/${salesOrderId}`, method: 'PUT', body: payload }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'SalesOrder', id: arg.salesOrderId }]
    }),
    submitSalesOrderHeader: builder.mutation<SalesOrderModel, SubmitHeaderSalesOrderParams>({
      query: ({ salesOrderId }) => ({ url: `productSalesOrders/${salesOrderId}`, method: 'POST' })
    }),
    submitSalesOrderLine: builder.mutation<SalesOrderLineItemModel, SubmitLineItemSalesOrderParams>({
      query: ({ salesOrderId, lineItemId }) => ({ url: `productSalesOrders/${salesOrderId}/lines/${lineItemId}`, method: 'POST' })
    }),
    validateSalesOrderLineItem: builder.mutation<boolean, ValidateSalesOrderLineItemParams>({
      query: ({ salesOrderId, lineItemId }) => ({ url: `productSalesOrders/${salesOrderId}/lines/${lineItemId}/validate`, method: 'GET' })
    }),
    createPurchaseOrder: builder.mutation<{ message: string }, { salesOrderId: string }>({
      query: ({ salesOrderId }) => ({ url: `productSalesOrders/${salesOrderId}/purchaseOrders`, method: 'POST' })
    }),
    postSalesOrder: builder.mutation<SalesOrderModel, { payload: SalesOrderPayloadModel; payloadSalesOrderId: string }>({
      async queryFn(_arg, _queryApi, _extraOptions, baseQuery) {
        const { payload, payloadSalesOrderId } = _arg;
        const state = _queryApi.getState() as ReduxState;

        const submittedCount = state.salesOrders.submittedCount;
        // let salesOrderId = _arg.payload?.id;
        let salesOrderResponse: QueryReturnValue<SalesOrderModel, FetchBaseQueryError, FetchBaseQueryMeta>;

        _queryApi.dispatch(setSubmitLoadingMessage({ message: 'Saving Sales Order...', addCount: false }));

        // Create new sales order or Update it
        if (!payloadSalesOrderId) {
          salesOrderResponse = (await baseQuery({ url: `productSalesOrders`, method: 'POST', body: payload })) as QueryReturnValue<SalesOrderModel, FetchBaseQueryError, FetchBaseQueryMeta>;
        } else {
          salesOrderResponse = (await baseQuery({ url: `productSalesOrders/${payloadSalesOrderId}`, method: 'PUT', body: payload })) as QueryReturnValue<
            SalesOrderModel,
            FetchBaseQueryError,
            FetchBaseQueryMeta
          >;
        }

        if (salesOrderResponse.error) return { error: salesOrderResponse.error };

        const salesOrderId = salesOrderResponse.data.id;

        _queryApi.dispatch(setSubmitLoadingMessage({ message: 'Validating Sales Order Header Info...', addCount: false }));
        // Validate
        const validateHeader = (await baseQuery({ url: `productSalesOrders/${salesOrderId}/validate`, method: 'GET' })) as QueryReturnValue<boolean, FetchBaseQueryError, FetchBaseQueryMeta>;

        if (validateHeader.error) return { error: validateHeader.error };
        const productLineItemIds = salesOrderResponse.data.lines.map((product) => product.id);
        // const customerLineNumbers = salesOrderResponse.data.lines.map((product) => ({ customerLinesNumber: product.customerLineNumber, productLineId: product.id }));

        _queryApi.dispatch(setSubmitLoadingMessage({ message: 'Validating Sales Order Line Items...', addCount: false }));

        // Validate Line Items
        const validateLineItems = (await Promise.all(
          productLineItemIds.map((lineId) => baseQuery({ url: `productSalesOrders/${salesOrderId}/lines/${lineId}/validate`, method: 'GET' }))
        )) as QueryReturnValue<boolean, FetchBaseQueryError, FetchBaseQueryMeta>[];

        if (validateLineItems.find((res) => res.error)) {
          _queryApi.dispatch(setLineItemSubmitErrors(validateLineItems));

          return {
            error: {
              status: 400,
              statusText: 'Bad Request',
              data: 'Failed to submit a product'
            }
          };
        }

        _queryApi.dispatch(setSubmitLoadingMessage({ message: 'Posting Sales Order Header Info...', addCount: false }));

        // Submit Header
        const headerResponse = (await baseQuery({ url: `productSalesOrders/${salesOrderId}`, method: 'POST' })) as QueryReturnValue<SalesOrderModel, FetchBaseQueryError, FetchBaseQueryMeta>;

        if (headerResponse.error) return { error: headerResponse.error };

        // _queryApi.dispatch(setSubmitLoadingMessage({ message: `Posting Sales Order Line Items ${submittedCount} of ${productLineItemIds.length}`, addCount: true }));

        // sort array in numerical order

        // SORTED BY CUSTOMER LINE NUMBER IF NEEDED

        // const sortedCustomerLineNumbers = orderBy(customerLineNumbers, ['customerLinesNumber'], ['asc']);
        const lineItemsResponse = <any>[];

        for await (const lineId of productLineItemIds) {
          const response = await baseQuery({ url: `productSalesOrders/${salesOrderId}/lines/${lineId}`, method: 'POST' });

          const productNumber = salesOrderResponse.data.lines.find((line) => line.id === lineId)?.productNumber;

          if (response.error) {
            _queryApi.dispatch(setLineItemSubmitErrors(response.error));
            // _queryApi.dispatch(setUnsuccessfulLineItems())

            lineItemsResponse.push({ data: response.error, productNumber });
            _queryApi.dispatch(setSubmitLoadingMessage({ message: `Posting Sales Order Line Items ${submittedCount} of ${productLineItemIds.length}`, addCount: true }));
            continue;
          }
          lineItemsResponse.push({ data: response.data, productNumber });
          _queryApi.dispatch(setSubmitLoadingMessage({ message: `Posting Sales Order Line Items ${submittedCount} of ${productLineItemIds.length}`, addCount: true }));
        }
        let poResponse: string | undefined = undefined

        if (salesOrderResponse.data.lines.some((line) => line.vendorId)) {
          _queryApi.dispatch(setSubmitLoadingMessage({ message: 'Creating Purchase Order...', addCount: false }));
          const response = await baseQuery({ url: `productSalesOrders/${salesOrderId}/purchaseOrders`, method: 'POST' });

          if (response.error)
            return {
              error: response.error
            };
          else {
            const responseMessage = (response as {data: {message: string}}).data.message

            poResponse = responseMessage
          }
        }

        // COMMENT OUT BATCH FOR SEQUENTIAL IMPLEMENTATION LEFT CODE FOR FUTURE USE

        // const batchedIds = splitArrayIntoChunksOfLength(productLineItemIds, 25) as string[][];

        // for await (const lineIds of batchedIds) {
        //   await Promise.allSettled(
        //     lineIds.map(async (lineId) => {
        //       const response = await baseQuery({ url: `productSalesOrders/${salesOrderId}/lines/${lineId}`, method: 'POST' });

        //       if (response.error) {
        //         _queryApi.dispatch(setLineItemSubmitErrors(response.error));

        //         return response.error;
        //       }

        //       lineItemsResponse.push(response.data);

        //       return response.data;
        //     })
        //   );

        //   _queryApi.dispatch(setSubmitLoadingMessage({ message: `Posting Sales Order Line Items ${submittedCount} of ${productLineItemIds.length}`, addCount: true }));
        // }
        // Submit Product Lines
        _queryApi.dispatch(resetSubmittedCount());

        // if (lineItemResponse.find((res) => res.error)) {
        //   _queryApi.dispatch(setLineItemSubmitErrors(lineItemResponse));

        //   return {
        //     error: {
        //       status: 400,
        //       statusText: 'Bad Request',
        //       data: 'Failed to submit a product'
        //     }
        //   };
        // }

        return { data: { ...headerResponse.data, updatedLines: lineItemsResponse, poResponse  } };
      }
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const {
  useGetSalesOrdersQuery,
  useGetSalesOrderQuery,
  useLazyGetSalesOrderQuery,
  useGetDeletedSalesOrdersQuery,
  useCreateSalesOrderMutation,
  useUpdateSalesOrderMutation,
  useDeleteAttachmentMutation,
  usePostAttachmentMutation,
  useSubmitSalesOrderHeaderMutation,
  useRecoverSalesOrderMutation,
  useCreateAttachmentMutation,
  useSubmitSalesOrderLineMutation,
  usePostSalesOrderMutation,
  useDeleteSalesOrderMutation,
  usePurgeSalesOrderMutation,
  useCreatePurchaseOrderMutation,
  useGetVersionQuery,
  useGetApiNameQuery
} = salesOrderApi;
